import React from "react";
import { Formik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  TextField,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { LAYOUT_HEIGHT } from "state/constants";
import api from "state/axios";
import StyledButton from "components/StyledButton";
import DateRangeField from "components/DateRangeField";
import { REQUEST_TYPE_OPTIONS } from "./constants";


const schema = yup.object({
  crewCode:     yup.string(),
  requestTypes: yup.array().min(1, "Select at least one type"),
});

const INITIAL_VALUES = {
  crewCode:     "",
  requestTypes: [],
};

export default function RequestForm({ onCreated }) {
  const now = new Date();
  const [startDate, setStartDate] = React.useState(
    new Date(now.getFullYear(), now.getMonth(), 1)
  );
  const [endDate, setEndDate] = React.useState(
    new Date(now.getFullYear(), now.getMonth() + 1, 0)
  );
  const [crewOptions, setCrewOptions] = React.useState([]);
  const [submitting, setSubmitting]   = React.useState(false);

  React.useEffect(() => {
    api.get("/crewmembers/active").then(({ data }) => {
      setCrewOptions(data || []);
    }).catch(() => {});
  }, []);

  const handleDateChange = ([start, end]) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleCreate = async (values, { resetForm }) => {
    if (!startDate || !endDate) {
      toast.error("Please select a date range.");
      return;
    }
    setSubmitting(true);

    try {
      await api.post("/datarequests/new", {
        startDate:    startDate.toISOString(),
        endDate:      endDate.toISOString(),
        crewCode:     values.crewCode,
        requestTypes: values.requestTypes,
      });
      toast.success("Request created.");
      resetForm();
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create request!");
    } finally {
      setSubmitting(false);
    }
  };

  
  return (
    <Card
      elevation={3}
      sx={{
        height: { xs: 'auto', md: LAYOUT_HEIGHT },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{          
          flex: 1, 
          minHeight: 0,
          display: "flex", 
          flexDirection: "column",
        }}
      >
        <Typography 
          variant="h6" 
          fontWeight={600} 
          mb={2}
        >
          New Request
        </Typography>
        <Formik 
          initialValues={INITIAL_VALUES} 
          validationSchema={schema} 
          onSubmit={handleCreate}          
        >
          {({ 
            values, 
            errors, 
            touched, 
            handleBlur, 
            handleSubmit, 
            setFieldValue 
          }) => (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box 
                display="flex" 
                flexDirection="column" 
                gap={2} 
                mb={2}
              >
                <DateRangeField
                  fullWidth
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
                />

                <Autocomplete
                  size="small"
                  options={crewOptions}
                  getOptionLabel={(opt) =>
                    typeof opt === "string" ? opt : `${opt.crewCode} - ${opt.crewName}`
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt.crewCode === (typeof val === "string" ? val : val.crewCode)
                  }
                  value={crewOptions.find((c) => c.crewCode === values.crewCode) || null}
                  onChange={(_, newVal) =>
                    setFieldValue("crewCode", newVal ? newVal.crewCode : "")
                  }
                  onBlur={handleBlur}
                  slotProps={{ paper: { sx: { fontSize: "0.7rem" } } }}
                  renderOption={(props, opt) => (
                    <li {...props} style={{ fontSize: "0.7rem" }}>
                      {opt.crewCode} - {opt.crewName}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Crew Member"
                      error={touched.crewCode && Boolean(errors.crewCode)}
                      helperText={touched.crewCode && errors.crewCode}
                    />
                  )}
                />
              </Box>

              <Box
                mb={2}
                sx={{
                  flex: { xs: 'none', md: 1 },
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Request Types
                  {touched.requestTypes && errors.requestTypes && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="error"
                      ml={1}
                    >
                      - {errors.requestTypes}
                    </Typography>
                  )}
                </Typography>
                <FormGroup
                  sx={{
                    flex: { xs: 'none', md: 1 },
                    minHeight: 0,
                    overflowY: { xs: 'visible', md: 'auto' },
                    flexWrap: "nowrap",
                  }}
                >
                  {REQUEST_TYPE_OPTIONS?.map(({ value, label }) => (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          size="small"
                          checked={values.requestTypes.includes(value)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...values.requestTypes, value]
                              : values.requestTypes.filter((t) => t !== value);
                            setFieldValue("requestTypes", next);
                          }}
                        />
                      }
                      label={<Typography variant="body2">{label}</Typography>}
                    />
                  ))}
                </FormGroup>
              </Box>

              <Box display="flex" justifyContent="flex-end">
                <StyledButton 
                  type="submit" 
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={18} color="inherit" /> : <Add />}
                </StyledButton>
              </Box>
            </Box>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}
